import authManager from "./authManager";

export default class Adapter {
  constructor(dataCallback) {
    this.name = 'OpenApiWireAdapter';

    this.dataCallback = dataCallback;
    this.config = {};
    this.abortController = null;

    this.retryCount = 0; // current retry count
    this.maxRetries = 3; // max retry times
    this.retryDelay = 1000;
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // cache timeout in milliseconds, default is 5mins

    this.authManager = authManager;

    // Request queue
    this.pendingRequests = new Map();
    this.requestQueue = [];
    this.isProcessingQueue = false;
  }

  // Lifecycle - create connect
  connect() {}

  // Lifecycle - update data
  update(config) {
    this.config = { ...config };
    this.retryCount = 0;

    if (this.shouldFetchData(config)) {
      this.fetchDataWithQueue(config);
    } else {
      this.dataCallback({ data: null, error: null });
    }
  }

  // Lifecycle - destroy
  disconnect() {
    if (this.abortController) {
      this.abortController.abort();
    }

    this.cache.clear();
    this.pendingRequests.clear();
    this.requestQueue = [];
    this.isProcessingQueue = false;
  }

  async waitForAuth() {
    try {
      if (!this.authManager.isReady()) {
        await this.authManager.waitForAuth();
      }
      return true;
    } catch(error) {
      throw error;
    }
  }

  getAccessToken() {
    return this.authManager.getAccessToken();
  }

  isAuthenticated() {
    return this.authManager.isAuthenticated();
  }

  shouldFetchData (config) {
    return config && config.endpoint && config.endpoint !== undefined;
  }

  async fetchDataWithQueue(config) {
    const requestKey = this.generateRequestKey(config);

    if (this.pendingRequests.has(requestKey)) {
      console.log('Request is inqueue');
      return;
    }

    this.requestQueue.push({ config, requestKey, callback: this.callback });
    this.pendingRequests.set(requestKey, true);

    try {
      await this.authManager.waitForAuth();
    } catch (error) {
      console.error('OpenApi Adapter: Authentication failed, no need to send any request');
      this.dataCallback({
        data: null,
        error: {
          message: `Auth failed: ${error.message}`,
          status: 'AUTH_FAILED',
          originalError: error
        }
      });
      this.pendingRequests.delete(requestKey);
      return;
    }

    if (!this.isProcessingQueue) {
      this.processRequestQueue();
    }
  }

  async processRequestQueue() { 
    this.isProcessingQueue = true;
    while (this.requestQueue.length > 0) {
      const request = this.requestQueue.shift();
      try {
        await this.fetchDataWithRetry(request.config);
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        this.pendingRequests.delete(request.requestKey);
      }

      // avoid too fast
      await this.delay(10);
    }
    this.isProcessingQueue = false;
  }

  async fetchDataWithRetry(config) { 
    const cacheKey = this.generateCacheKey(config);
    
    const cachedData = this.getCachedData(cacheKey);
    if (cachedData) {
      this.dataCallback(cacheData);
      return;
    }

    let lastError = null;
    for (let attempt = 0; attempt < this.maxRetries; attempt++) {
      try {
        const result = await this.performRequest(config);

        this.setCacheData(cacheKey, result);
        this.dataCallback(result);
        return;
      } catch (error) {
        lastError = error;

        if (attempt < this.maxRetries) {
          await this.delay(this.retryDelay * Math.pow(2, attempt));
        }
      }
    }

    this.dataCallback({
      data: null,
      error: {
        message: lastError.message,
        status: lastError.status || 'NETWORK_ERROR',
        retries: this.maxRetries
      }
    })
  }

  async performRequest(config) {
    const { endpoint, method = 'GET', params = {}, headers = {} } = config;

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    let url = endpoint;
    if (method === 'GET' && params) {
      const searchParams = new URLSearchParams(params);
      url = `${url}?${searchParams.toString()}`
    }

    const customHeader = {
      'Content-Type': 'application/json',
      ...headers
    }
    if (this.authManager.getAccessToken()) {
      customHeader['access_token'] = this.authManager.getAccessToken()
    }
    const requestOptions = {
      method,
      headers: customHeader,
      body: method === 'GET' ? null : JSON.stringify(params),
      signal: this.abortController.signal
    }

    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    if (data.status === true && data.errorCode === '0') {
      return { data: data.data, error: null };
    } else if (data.errorCode === '401') {
      await this.authManager.refreshAccessToken();
      return this.performRequest(config);
    }

    return {
      data: null,
      error: {
        message: data.message,
        errorCode: data.errorCode,
        status: data.status
      }
    }
  }

  getCachedData(cacheKey) {
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    if (cached) {
      this.cache.delete(cacheKey);
    }
    return null;
  }

  setCacheData(cacheKey, data) {
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });

    if (this.cache.size > 100) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
  }

  generateCacheKey(config) {
    return JSON.stringify({
      endpoint: config.endpoint,
      params: config.params,
      method: config.method || 'GET'
    })
  }

  generateRequestKey(config) {
    return this.generateCacheKey(config);
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  static clearAllCache() {
    this.cache = new Map();
  }

  static configure(options) {
    if (options.maxRetries !== undefined) {
      OpenApiWireAdapter.prototype.maxRetries = options.maxRetries;
    }

    if (options.retryDelay !== undefined) {
      OpenApiWireAdapter.prototype.retryDelay = options.retryDelay;
    }

    if (options.cacheTimeout !== undefined) {
      OpenApiWireAdapter.prototype.cacheTimeout = options.cacheTimeout;
    }
  }
}
